import { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import {
  fetchEstated,
  fetchRisk,
  fetchDistanceToFirehydrant,
  fetchMortgageRates,
} from "api";
import { Box, Toolbar, useMediaQuery } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import AppBar from "components/App/AppBar";
import Drawer, { estatedItems } from "components/App/Drawer";
import Dashboard from "components/Dashboard/Dashboard";

const Main = styled("main")(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-240px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

export default function App(props) {
  const location = useLocation();
  const item = useMemo(() => {
    return (
      estatedItems
        .flatMap((_) => _.items)
        .find((_) => _.key === location.pathname.slice(1)) || {
        key: "summary",
        label: "Summary",
      }
    );
  }, [location]);
  const [selectedItem, setSelectedItem] = useState(item);
  useEffect(() => {
    setSelectedItem(item);
  }, [item]);
  const [estatedProperty, setEstatedProperty] = useState(null);
  const [risk, setRisk] = useState(null);
  const [firehydrants, setFirehydrants] = useState(null);
  const [loading, setLoading] = useState(false);
  const greaterThan1366 = useMediaQuery("(min-width:1366px)");
  const [open, setOpen] = useState(greaterThan1366);
  const [searchParams, setSearchParams] = useSearchParams();
  const [newParams, setNewParams] = useState(null);
  const [place, setPlace] = useState({
    formatted_street_address: searchParams.get("formatted_street_address"),
    city: searchParams.get("city"),
    state: searchParams.get("state"),
    zip_code: searchParams.get("zip_code"),
    county: searchParams.get("county"),
    lat: parseFloat(searchParams.get("lat")),
    lng: parseFloat(searchParams.get("lng")),
  });

  const onPlaceSelected = async (params) => {
    setFirehydrants(null);
    setEstatedProperty(null);
    setRisk(null);
    setLoading(true);
    const estatedPromise = fetchEstated({
      ...params,
      email: props.profile.email,
      first_name: props.profile.given_name,
      last_name: props.profile.family_name,
    });
    const riskPromise = fetchRisk(params);
    fetchDistanceToFirehydrant(params).then((val) => {
      if (val && val.closest) {
        setFirehydrants(
          Math.round(val.closest.distance).toLocaleString() + " feet"
        );
      } else if (val) {
        setFirehydrants("1,000+ feet");
      } else {
        setFirehydrants("No results returned");
      }
    });
    Promise.all([estatedPromise, riskPromise])
      .then((responses) => {
        setEstatedProperty(responses[0]);
        setRisk(responses[1]);
        setNewParams(params);
        setPlace(params);
        document.title = `${params.formatted_street_address}, ${params.city}, ${params.state} ${params.zip_code} - Obie - Maxim`;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const containsAllParams =
      Object.keys(place)
        .map((_) => place[_])
        .filter((_) => !_).length === 0;
    if (containsAllParams && !estatedProperty) {
      onPlaceSelected(place);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  useEffect(() => {
    if (newParams) {
      setSearchParams(newParams);
    }
  }, [newParams, setSearchParams]);

  useEffect(() => {
    setOpen(greaterThan1366);
  }, [greaterThan1366]);

  const toggleDrawer = () => setOpen(!open);

  const [mortgageRates, setMortgageRates] = useState(null);
  useEffect(() => {
    fetchMortgageRates().then((res) => {
      setMortgageRates(res);
    });
  }, []);

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        open={open}
        place={place}
        profile={props.profile}
        onPlaceSelected={onPlaceSelected}
        toggleDrawer={toggleDrawer}
        setProfile={props.setProfile}
      />
      <Drawer open={open} selectedItem={selectedItem} />
      <Main open={open}>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.grey[100],
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Dashboard
            loading={loading}
            selectedItem={selectedItem}
            estatedProperty={estatedProperty}
            risk={risk}
            firehydrants={firehydrants}
            profile={props.profile}
            mortgageRates={mortgageRates}
          />
        </Box>
      </Main>
    </Box>
  );
}
